/**
 * Dedicated resident planner: Overdue strip + pageable month grid + selected-
 * day list with the same CRUD as the home widget (shared TodoFormModal, same
 * useResidentTodos seam — RTK Query dedupes the fetch between surfaces).
 * One responsive component for both navigators (mobile 'ResidentPlanner',
 * desktop 'Planner'): stacked on mobile/tablet, two columns on desktop.
 * State model mirrors the widget: a single `selectedDate`; the visible month
 * derives from it and ‹ › jump to the 1st of the adjacent month, so day
 * clamping can't happen.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  ScrollView,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { PageHeader } from '@/components/shared/redesign/PageHeader';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import {
  MonthGrid,
  OverdueSection,
  TodoRow,
  TodoFormModal,
  type TodoFormValues,
} from '@/components/residents/redesign/todoCalendar';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useResidentTodos } from '@/hooks/residents/useResidentTodos';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import {
  addMonthsToStart,
  formatDayHeading,
  groupTodosByDateKey,
  sortTodos,
  toDateKey,
} from '@/utils/residents/todoCalendar';
import type { ResidentTodo } from '@/types/residents/todos';

const HP = REDESIGN_SPACING.horizontalPadding;
// Match the payments/events desktop content frame.
const MAX_CONTENT_WIDTH = 1100;

interface PlannerScreenProps {
  /** Test seam: pins "today". Defaults to the device clock at mount. */
  todayOverride?: Date;
}

export const PlannerScreen: React.FC<PlannerScreenProps> = ({
  todayOverride,
}) => {
  const route = useRoute();
  const showBackLink =
    (route.params as { showBackLink?: boolean } | undefined)?.showBackLink ===
    true;
  const insets = useSafeAreaInsets();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';

  const [today] = useState<Date>(() => todayOverride ?? new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [formVisible, setFormVisible] = useState(false);
  const [formTodo, setFormTodo] = useState<ResidentTodo | null>(null);

  const {
    todos,
    toggleTodo,
    isLoading,
    isError,
    refetch,
    addTodo,
    updateTodo,
    deleteTodo,
  } = useResidentTodos();

  const type = useTypography();
  const space = useSpacing();
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      scroll: {
        flex: 1,
      },
      content: {
        paddingHorizontal: HP,
        width: '100%',
        maxWidth: MAX_CONTENT_WIDTH,
        alignSelf: 'center',
      },
      // Payments/events pad the top of the frame; mobile keeps its own rhythm.
      contentDesktop: {
        paddingTop: HP,
      },
      backLink: {
        marginBottom: -4,
      },
      columns: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: space.xl,
      },
      columnLeft: {
        flex: 1,
        gap: space.lg,
      },
      columnRight: {
        flex: 1,
      },
      stacked: {
        gap: space.lg,
      },
      card: {
        gap: space.lg,
      },
      dayHeadingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 44,
      },
      // Baseline row instead of the widget's nested <Text>: same inline look
      // ("Wed, Aug 12 · 1 due"), but the day label stays its own text node.
      headingGroup: {
        flexDirection: 'row',
        alignItems: 'baseline',
        flexShrink: 1,
      },
      heading: {
        ...type.cardTitleLarge,
        color: colors.text.primary,
      },
      headingCount: {
        ...type.small,
        color: colors.text.muted,
      },
      addText: {
        ...type.button,
        color: semantic.interactive.primaryHover, // AA-safe blue ink
      },
      emptyWrap: {
        alignItems: 'center',
        paddingVertical: space['3xl'],
      },
      emptyText: {
        ...type.body,
        color: colors.text.muted,
      },
      retryText: {
        ...type.button,
        color: semantic.interactive.primaryHover,
        paddingTop: space.sm,
      },
    }),
  );

  const todayKey = toDateKey(today);
  const selectedKey = toDateKey(selectedDate);

  const groups = useMemo(() => groupTodosByDateKey(todos), [todos]);
  const countsByKey = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [key, list] of Object.entries(groups)) counts[key] = list.length;
    return counts;
  }, [groups]);
  const dayTodos = useMemo(
    () => sortTodos(groups[selectedKey] ?? []),
    [groups, selectedKey],
  );
  const dueCount = useMemo(
    () => dayTodos.filter(t => !t.completed).length,
    [dayTodos],
  );
  // Date-key strings compare correctly ('2026-08-09' < '2026-08-12').
  const overdueTodos = useMemo(
    () =>
      todos
        .filter(t => !t.completed && t.dueDate < todayKey)
        .sort(
          (a, b) =>
            a.dueDate.localeCompare(b.dueDate) ||
            a.title.localeCompare(b.title),
        ),
    [todos, todayKey],
  );

  const handlePrevMonth = useCallback(
    () => setSelectedDate(d => addMonthsToStart(d, -1)),
    [],
  );
  const handleNextMonth = useCallback(
    () => setSelectedDate(d => addMonthsToStart(d, 1)),
    [],
  );
  const handleAdd = useCallback(() => {
    setFormTodo(null);
    setFormVisible(true);
  }, []);
  const handleEdit = useCallback((todo: ResidentTodo) => {
    setFormTodo(todo);
    setFormVisible(true);
  }, []);
  const handleCloseForm = useCallback(() => setFormVisible(false), []);
  // The modal owns try/catch; an edit save always sends notes explicitly
  // (null clears it server-side) — same contract as the widget.
  const handleSaveForm = useCallback(
    (values: TodoFormValues) =>
      formTodo
        ? updateTodo({
            id: formTodo.id,
            title: values.title,
            dueDate: values.dueDate,
            notes: values.notes ?? null,
          })
        : addTodo(values),
    [formTodo, updateTodo, addTodo],
  );

  const overdueCard =
    overdueTodos.length > 0 ? (
      <GlassCard style={styles.card}>
        <OverdueSection
          todos={overdueTodos}
          onToggle={toggleTodo}
          onEdit={handleEdit}
        />
      </GlassCard>
    ) : null;

  const monthCard = (
    <GlassCard style={styles.card}>
      <MonthGrid
        monthAnchor={selectedDate}
        selectedKey={selectedKey}
        todayKey={todayKey}
        countsByKey={countsByKey}
        onSelectDay={setSelectedDate}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
      />
    </GlassCard>
  );

  const dayCard = (
    <GlassCard style={styles.card}>
      <View style={styles.dayHeadingRow}>
        {/* Two sibling <Text>s so the shallow test flattener still finds the
            day label on its own, wrapped in one `accessible` group so
            VoiceOver reads "Wed, Aug 12, 1 due" as a single stop instead of
            leaving "· 1 due" orphaned. */}
        <View
          style={styles.headingGroup}
          accessible
          accessibilityLabel={`${formatDayHeading(selectedDate)}${
            dueCount > 0 ? `, ${dueCount} due` : ''
          }`}
        >
          <Text style={styles.heading}>{formatDayHeading(selectedDate)}</Text>
          {dueCount > 0 ? (
            <Text style={styles.headingCount}>{` · ${dueCount} due`}</Text>
          ) : null}
        </View>
        <TouchableOpacity
          onPress={handleAdd}
          // 14 per side reaches the 44pt target around ~17pt text (TodoRow's
          // pencil precedent).
          hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
          accessibilityRole="button"
          accessibilityLabel="Add todo"
        >
          <Text style={styles.addText}>+ Add</Text>
        </TouchableOpacity>
      </View>
      {isLoading ? (
        <View style={styles.emptyWrap}>
          <Text style={styles.emptyText}>Loading your todos…</Text>
        </View>
      ) : isError && todos.length === 0 ? (
        // Same rule as the widget: only a failure with NOTHING cached takes
        // over the body — a failed background refetch with rows in hand keeps
        // the list, and row-level rollback is the feedback.
        <View style={styles.emptyWrap}>
          <Text style={styles.emptyText}>Couldn't load your todos.</Text>
          <TouchableOpacity
            onPress={refetch}
            accessibilityRole="button"
            accessibilityLabel="Retry loading todos"
            // 14 per side reaches the 44pt target around ~17pt text.
            hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
          >
            <Text style={styles.retryText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : dayTodos.length === 0 ? (
        <View style={styles.emptyWrap}>
          <Text style={styles.emptyText}>Nothing due this day</Text>
        </View>
      ) : (
        <View>
          {dayTodos.map((todo, index) => (
            <TodoRow
              key={todo.id}
              todo={todo}
              isLast={index === dayTodos.length - 1}
              onToggle={toggleTodo}
              onEdit={handleEdit}
            />
          ))}
        </View>
      )}
    </GlassCard>
  );

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={[
        styles.content,
        isDesktop && styles.contentDesktop,
        { paddingBottom: 40 + insets.bottom },
      ]}
      showsVerticalScrollIndicator={false}
    >
      {showBackLink && <ScreenBackLink style={styles.backLink} />}
      <PageHeader title="Planner" />
      {isDesktop ? (
        <View style={styles.columns}>
          <View style={styles.columnLeft}>
            {overdueCard}
            {monthCard}
          </View>
          <View style={styles.columnRight}>{dayCard}</View>
        </View>
      ) : (
        <View style={styles.stacked}>
          {overdueCard}
          {monthCard}
          {dayCard}
        </View>
      )}
      <TodoFormModal
        visible={formVisible}
        todo={formTodo}
        initialDateKey={selectedKey}
        onSave={handleSaveForm}
        onDelete={deleteTodo}
        onClose={handleCloseForm}
      />
    </ScrollView>
  );
};

PlannerScreen.displayName = 'PlannerScreen';
